import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import Field from '../components/Field';
import Callout from '../components/Callout';
import { VERTICALS, VERTICAL_GROUPS, type Vertical } from '../data/verticals';
import { api } from '../lib/api';
import { cx } from '../lib/cx';

/**
 * Placeholder for the vertical's hero photograph. The style library does not
 * exist yet (phase 5), so the tile carries the building mark on a green wash.
 * When photography lands this becomes an <img> and nothing else changes.
 */
function TileArt({ dimmed }: { dimmed: boolean }) {
  return (
    <div
      className={cx(
        'flex aspect-[16/9] items-center justify-center border-b border-grey-200 transition-colors',
        dimmed ? 'bg-grey-050' : 'bg-green-050',
      )}
      aria-hidden="true"
    >
      <svg viewBox="0 0 32 32" className="h-12 w-12">
        <g
          fill="none"
          stroke="var(--color-green-500)"
          strokeWidth="2.4"
          strokeLinejoin="round"
          strokeLinecap="round"
        >
          <path d="M3 27h26" />
          <path d="M6 27V14h11v13" />
          <path d="M17 27V7h9v20" />
        </g>
      </svg>
    </div>
  );
}

export default function NewProject() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [starting, setStarting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return VERTICALS;
    return VERTICALS.filter((v) =>
      `${v.label} ${v.blurb} ${v.group}`.toLowerCase().includes(q),
    );
  }, [query]);

  const start = async (v: Vertical) => {
    if (starting) return;
    setStarting(v.id);
    setError(null);
    try {
      const project = await api.createProject(v.id);
      navigate(`/project/${project.id}/questions`);
    } catch {
      setError('We could not start your project. Try again in a moment.');
      setStarting(null);
    }
  };

  const groups = VERTICAL_GROUPS.map((g) => ({
    name: g,
    items: matches.filter((v) => v.group === g),
  })).filter((g) => g.items.length > 0);

  return (
    <AppShell>
      <p className="t-eyebrow text-green-600">Start a project</p>
      <h1 className="t-h1 mt-3 text-ink">What are you building?</h1>
      <p className="t-body-lg measure mt-4 text-grey-600">
        Pick the closest match. You can change it later, and if nothing here fits, pick
        the nearest one and tell us more in the questions.
      </p>

      <div className="mt-8 max-w-md">
        <Field
          id="vertical-search"
          label="Search building types"
          placeholder="Church, warehouse, clinic…"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error && (
        <div className="mt-6">
          <Callout tone="caution" title="That did not go through">
            {error}
          </Callout>
        </div>
      )}

      <p className="t-meta mt-6 text-grey-600" aria-live="polite">
        <span className="t-numeral">{matches.length}</span>{' '}
        {matches.length === 1 ? 'building type' : 'building types'}
        {query.trim() && ' match your search'}
      </p>

      {groups.length === 0 ? (
        <div className="mt-8">
          <Callout title="Nothing matched that">
            Try a broader word — &ldquo;retail&rdquo; instead of a brand name, or
            &ldquo;shop&rdquo; instead of a trade. You can also pick the closest building
            type and describe the rest in the questions.
          </Callout>
        </div>
      ) : (
        <div className="mt-10 flex flex-col gap-12">
          {groups.map((group) => (
            <section key={group.name}>
              <h2 className="t-h2 text-ink">{group.name}</h2>
              <ul className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {group.items.map((v) => {
                  const busy = starting === v.id;
                  return (
                    <li key={v.id}>
                      <button
                        type="button"
                        onClick={() => void start(v)}
                        disabled={starting !== null}
                        aria-busy={busy}
                        className={cx(
                          'group h-full w-full overflow-hidden rounded-lg border border-grey-200 bg-white text-left shadow-sm transition-colors',
                          'hover:border-green-600 disabled:cursor-wait',
                          starting !== null && !busy && 'opacity-50',
                        )}
                      >
                        <TileArt dimmed={starting !== null && !busy} />
                        <span className="block px-5 py-5">
                          <span className="t-h3 block text-ink">{v.label}</span>
                          <span className="t-meta mt-2 block text-grey-600">{v.blurb}</span>
                          <span className="t-meta mt-4 block font-semibold text-green-700">
                            {busy ? 'Starting…' : 'Start with this'}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </AppShell>
  );
}
